import React, { useEffect, useState } from 'react';
import { GoogleLogin, CredentialResponse } from '@react-oauth/google';
import { useAuth } from '../contexts/AuthContext';
import { getEnv } from '../utils/env';
import { ONE_TAP_GRACE_MS } from '../constants/auth';

const LoginScreen: React.FC = () => {
  const { loginWithGoogleJwt, authError, isLoading } = useAuth();
  const [devToken, setDevToken] = useState<string>(() => getEnv('VITE_DEV_ACCESS_TOKEN') || '');
  // Startwert an AuthContext koppeln: hat dessen Gnadenfrist (z.B. bei Direktaufruf
  // einer geschützten URL) schon vor dem Mount von LoginScreen gegriffen, soll hier
  // keine zweite Wartezeit von vorne beginnen.
  const [isWaitingForOneTap, setIsWaitingForOneTap] = useState(() => isLoading);
  const isDevMode = import.meta.env.DEV || getEnv('VITE_ENABLE_MOCK_AUTH') === 'true';
  const showManualLogin = !!authError || !isWaitingForOneTap;

  useEffect(() => {
    if (!isLoading) {
      setIsWaitingForOneTap(false);
      return;
    }
    const timeout = window.setTimeout(() => setIsWaitingForOneTap(false), ONE_TAP_GRACE_MS);
    return () => window.clearTimeout(timeout);
  }, [isLoading]);

  const handleSuccess = (credentialResponse: CredentialResponse) => {
    if (credentialResponse.credential) {
      loginWithGoogleJwt(credentialResponse.credential);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 dark:bg-gray-900 p-4">
      <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 space-y-6">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            360Volt Dokumentation
          </h1>
          <p className="text-gray-500 dark:text-gray-400">
            Anmelden um fortzufahren
          </p>
        </div>

        {/* Falsches Konto */}
        {authError === 'wrong-account' && (
          <div className="bg-red-50 dark:bg-red-900 text-red-800 dark:text-red-200 p-3 rounded text-sm">
            Dieses Google-Konto ist nicht erlaubt. Bitte melde dich mit deiner{' '}
            <strong>@360volt.de</strong> E-Mail-Adresse an.
          </div>
        )}

        <div className="flex flex-col items-center gap-3">
          {showManualLogin ? (
            <GoogleLogin
              onSuccess={handleSuccess}
              onError={() => {}}
              text="signin_with"
              shape="rectangular"
              width="320"
            />
          ) : (
            <p className="text-sm text-gray-400 dark:text-gray-500">Anmeldung läuft…</p>
          )}

          {isLoading && (
            <p className="text-sm text-gray-400 dark:text-gray-500">Bitte warten...</p>
          )}
        </div>

        {/* Developer Modus */}
        {isDevMode && (
          <>
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-dashed border-yellow-400 dark:border-yellow-600"></div>
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="px-2 bg-white dark:bg-gray-800 text-yellow-600 dark:text-yellow-400 font-mono">
                  DEV
                </span>
              </div>
            </div>
            <div className="space-y-2">
              <label className="block text-xs font-medium text-yellow-700 dark:text-yellow-400">
                Access Token (Bearer)
              </label>
              <textarea
                value={devToken}
                onChange={(e) => setDevToken(e.target.value)}
                placeholder="Token hier einfügen..."
                rows={3}
                className="w-full text-xs font-mono resize-none rounded border border-yellow-300 dark:border-yellow-700 bg-yellow-50 dark:bg-gray-900 text-gray-800 dark:text-gray-200 p-2 focus:outline-none focus:ring-1 focus:ring-yellow-400"
              />
              <button
                type="button"
                onClick={() => loginWithGoogleJwt(devToken.trim())}
                disabled={isLoading || !devToken.trim()}
                className="w-full py-2 px-4 rounded border border-yellow-400 dark:border-yellow-600 bg-yellow-50 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200 text-sm hover:bg-yellow-100 dark:hover:bg-yellow-800 disabled:opacity-50 transition-colors"
              >
                Mit Token anmelden
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default LoginScreen;
